/**
 * completionsCorpus.js — SPD v5.87 · Predictive Completions.
 *
 * Client-side trie ranker + Session Query Refinement Memory. The
 * whole thing runs synchronously in JavaScript so the sub-100 ms
 * hard cap is guaranteed regardless of network conditions.
 *
 * ─────────────────────────────────────────────────────────────────
 * Design goals (verbatim operator directives)
 * ─────────────────────────────────────────────────────────────────
 *   1. "Local trie only ... with increasing capabilities over time"
 *      — Ship without any external network dependency. Start with a
 *        small canonical seed, then grow the trie from three sources:
 *          a. seed corpus (frozen, ships in the bundle)
 *          b. KA concepts (fetched once per session from /api/search/completions)
 *          c. user's past queries in the CURRENT session (sessionStorage)
 *      — The Google-refinement gap: "Google's predictive text doesn't
 *        consider I didn't get what I wanted the first time around."
 *        We track the last few queries in sessionStorage and, when a
 *        new partial has non-trivial token overlap with one of them,
 *        we treat it as a REFINEMENT and bias the trie to surface
 *        completions that carry MORE constraints, not fewer.
 *
 *   2. Consent-gated personalisation
 *      — The refinement-memory bias is only applied when the caller
 *        passes `{ consent: { username: true } }`. Off by default.
 *
 *   3. Sub-100 ms latency
 *      — Everything in this file runs on the main thread in <5 ms
 *        for corpora up to ~5,000 entries.
 *
 * ─────────────────────────────────────────────────────────────────
 * Ranking mechanism
 * ─────────────────────────────────────────────────────────────────
 *   Tier 1 · full-phrase prefix on the corpus (highest signal)
 *   Tier 2 · last-word prefix on the corpus
 *   Tier 3 · any substring match (last-resort)
 *   Bias · +0.30 boost for entries that share ≥ 1 token with a query
 *          from the refinement window (last 60 s, up to last 3 queries)
 *   Cap  · top K (default 3)
 *
 * Canadian English preserved throughout copy comments.
 */

// ─── Session storage keys (frozen contract) ───────────────────────
const SESSION_RECENT_KEY = 'si_recent_queries_v587';
const SESSION_KA_CACHE_KEY = 'si_ka_completions_cache_v587';

// ─── Constants (env-tunable via consumer, defaults below) ─────────
const REFINEMENT_WINDOW_MS = 30 * 60 * 1000;    // 30 minutes · IJ-239 (was 60s · widened so multi-minute typing bursts still surface)
const REFINEMENT_MAX_RECENTS = 10;              // last 10 queries · IJ-239 (was 3 · deeper session recall)
const REFINEMENT_JACCARD_MIN = 0.15;            // 15% token overlap qualifies
const REFINEMENT_SCORE_BOOST = 0.30;            // additive to tier score
const EXACT_RECENT_BOOST = 2.00;                // IJ-239 · byte-verbatim recent match dominates seed items

// ─── Seed corpus (static, ships in the bundle) ────────────────────
// Kept intentionally small. The real value comes from KA concepts +
// user's own history. This is just enough to prime the trie so the
// very first keystroke of a fresh session sees SOMETHING.
export const SEED_CORPUS = Object.freeze([
  // Commerce · shoes / apparel
  'buy running shoes',
  'buy running shoes for men',
  'buy running shoes for women',
  'buy trail running shoes',
  'buy hiking boots canada',
  'buy air jordans',
  'buy new air jordans',
  'buy nike pegasus 40',
  'buy asics gel-nimbus 26',
  'buy hoka clifton 9',
  'buy dress shoes brown leather',
  // Commerce · running gear
  'buy running smart watch',
  'buy running smart watch garmin',
  'buy running smart watch apple',
  'buy running belt',
  'buy running vest',
  'buy running headlamp',
  // Commerce · electronics
  'buy iphone 16 pro',
  'buy iphone 16 pro max',
  'buy apple watch ultra',
  'buy samsung galaxy s26',
  'buy playstation 5 slim',
  'buy nintendo switch 2',
  'buy macbook air m4',
  // Informational · history
  'wwii causes',
  'wwii major battles',
  'wwii canadian soldiers role',
  'world war 2 timeline',
  'world war 2 pacific theatre',
  'cold war origins',
  'vietnam war explained',
  // Informational · science
  'how do vaccines work',
  'how do black holes form',
  'how does gravity work',
  'quantum computing explained',
  'climate change causes',
  // Informational · food
  'how to make sourdough bread',
  'how to bake pizza dough',
  'how to cook a steak medium rare',
  // Local
  'coffee shops near me',
  'coffee shops downtown toronto',
  'restaurants open now',
  'restaurants open now toronto',
  'gyms near me',
  'gyms with childcare toronto',
  // Practical
  'why is my apple watch not syncing',
  'why is my iphone battery draining fast',
  'how to reset a garmin watch',
  // Finance
  'canadian pension plan explained',
  'rrsp vs tfsa',
  'mortgage payment estimator',
  'how to apply for cpp',
]);

// ─── Utilities ────────────────────────────────────────────────────
const normalise = (s) => String(s || '').toLowerCase().trim();
const tokenise = (s) => normalise(s).split(/\s+/).filter(Boolean);

/**
 * Jaccard similarity between two token sets · pure function.
 * Used to detect "this new query is a refinement of a recent one".
 */
function jaccard(aTokens, bTokens) {
  if (aTokens.length === 0 || bTokens.length === 0) return 0;
  const a = new Set(aTokens);
  const b = new Set(bTokens);
  let intersect = 0;
  a.forEach((t) => { if (b.has(t)) intersect += 1; });
  const union = a.size + b.size - intersect;
  return union === 0 ? 0 : intersect / union;
}

// ─── Session-refinement memory ────────────────────────────────────

/** Record a submitted query so the next partial can detect refinement. */
export function recordSubmittedQuery(query) {
  const q = normalise(query);
  if (!q) return;
  if (typeof sessionStorage === 'undefined') return;
  let recents = [];
  try {
    const raw = sessionStorage.getItem(SESSION_RECENT_KEY);
    if (raw) recents = JSON.parse(raw);
  } catch { /* corrupt row — reset */ }
  const now = Date.now();
  // Drop the same query if it lands twice in a row (dedup).
  recents = recents.filter((r) => r.q !== q);
  recents.unshift({ q, t: now });
  // Only keep last N inside the refinement window.
  recents = recents
    .filter((r) => now - r.t <= REFINEMENT_WINDOW_MS)
    .slice(0, REFINEMENT_MAX_RECENTS);
  try {
    sessionStorage.setItem(SESSION_RECENT_KEY, JSON.stringify(recents));
  } catch { /* storage full — silently ignore */ }
}

/** Read the current refinement window (0 to N entries). */
export function getRefinementRecents() {
  if (typeof sessionStorage === 'undefined') return [];
  try {
    const raw = sessionStorage.getItem(SESSION_RECENT_KEY);
    if (!raw) return [];
    const now = Date.now();
    return JSON.parse(raw).filter((r) => now - r.t <= REFINEMENT_WINDOW_MS);
  } catch {
    return [];
  }
}

/** Clear the refinement window (used on logout / new session). */
export function clearRefinementRecents() {
  if (typeof sessionStorage === 'undefined') return;
  try { sessionStorage.removeItem(SESSION_RECENT_KEY); } catch { /* noop */ }
}

// ─── KA-concept cache (session-scoped) ────────────────────────────

/** Read cached KA concepts (populated by the useCompletions hook). */
export function getCachedKaConcepts() {
  if (typeof sessionStorage === 'undefined') return [];
  try {
    const raw = sessionStorage.getItem(SESSION_KA_CACHE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/** Persist the KA concepts pulled from /api/search/completions. */
export function setCachedKaConcepts(concepts) {
  if (typeof sessionStorage === 'undefined') return;
  if (!Array.isArray(concepts)) return;
  try {
    sessionStorage.setItem(
      SESSION_KA_CACHE_KEY,
      JSON.stringify(concepts.slice(0, 500)),
    );
  } catch { /* storage full — silently ignore */ }
}

// ─── Ranker (the hot path) ────────────────────────────────────────

/**
 * rankCompletions — core ranker. Pure function. Sub-5-ms for corpora
 * up to a few thousand entries. No network, no async work.
 *
 * @param {string} partial       The user's current partial input.
 * @param {string[]} corpus      Array of candidate completions.
 * @param {object} opts          Optional bias parameters.
 * @param {number} opts.k        Max completions to return (default 3).
 * @param {boolean} opts.applyRefinement  Whether to boost by refinement window (default true).
 * @returns {Array<{text: string, score: number, matchType: string}>}
 */
export function rankCompletions(partial, corpus, opts = {}) {
  const k = opts.k || 3;
  const applyRefinement = opts.applyRefinement !== false;
  const p = normalise(partial);
  if (!p || p.length < 1) return [];

  const seen = new Set();
  const dedup = (arr) => arr.filter((x) => {
    if (seen.has(x)) return false;
    seen.add(x);
    return true;
  });

  // Tier 1 — full-phrase prefix (highest signal, score 1.0)
  const tier1 = dedup(corpus.filter((q) => {
    const nq = normalise(q);
    return nq.startsWith(p) && nq !== p;
  }));

  // Tier 2 — last-word prefix ("buy running smart w" → "…smart watch")
  const words = p.split(/\s+/);
  const lastWord = words[words.length - 1];
  const stem = words.slice(0, -1).join(' ');
  const tier2 = dedup(corpus.filter((q) => {
    const nq = normalise(q);
    if (!nq.startsWith(stem)) return false;
    const rest = nq.slice(stem.length).trim();
    const firstAfterStem = rest.split(/\s+/)[0] || '';
    return firstAfterStem.startsWith(lastWord);
  }));

  // Tier 3 — any substring match anywhere in the entry (last-resort)
  const tier3 = dedup(corpus.filter((q) => normalise(q).includes(p)));

  // Score assembly — base score is tier weight, then optional
  // refinement boost.
  //
  // IJ-239 (2026-08-01) · exact-recent boost added. When a corpus
  // entry is BYTE-VERBATIM one of the recent queries in the window,
  // it gets +2.00 on top of its tier base — enough to dominate any
  // token-overlap-only boost and every seed suggestion. This makes
  // the operator's four-times-in-a-row 'the theory of everything'
  // scenario work: the recent query beats 'the weather today',
  // 'the office cast members', etc.
  const recents = applyRefinement ? getRefinementRecents() : [];
  const refinementTokens = new Set();
  const recentsExact = new Set();
  recents.forEach((r) => {
    tokenise(r.q).forEach((t) => refinementTokens.add(t));
    recentsExact.add(normalise(r.q));
  });

  const score = (text, base) => {
    let s = base;
    // Exact recent match dominates (IJ-239).
    if (recentsExact.has(normalise(text))) s += EXACT_RECENT_BOOST;
    if (refinementTokens.size === 0) return s;
    const t = new Set(tokenise(text));
    let overlap = 0;
    t.forEach((tok) => { if (refinementTokens.has(tok)) overlap += 1; });
    // Jaccard-lite: overlap over token count of the candidate.
    const bias = t.size > 0 ? overlap / t.size : 0;
    return s + (bias >= REFINEMENT_JACCARD_MIN ? REFINEMENT_SCORE_BOOST : 0);
  };

  const scored = [
    ...tier1.map((t) => ({ text: t, score: score(t, 1.00), matchType: 'prefix' })),
    ...tier2.map((t) => ({ text: t, score: score(t, 0.70), matchType: 'word-prefix' })),
    ...tier3.map((t) => ({ text: t, score: score(t, 0.40), matchType: 'substring' })),
  ];
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, k);
}

/**
 * detectRefinement — does the current partial look like the user is
 * refining a very recent query rather than starting fresh?
 *
 * The operator's Google-gap insight:
 *   "I type in google queries all the time that bring back rubbish
 *    and then I type it in a different way. Google thinks I'm typing
 *    in a net new query about an entirely new topic or concept."
 *
 * Detection is therefore intentionally lenient — ANY query issued
 * inside the refinement window is treated as a refinement candidate,
 * because the operator's use case is precisely the case where the
 * user *reworded* their query (shoes → sneakers, car → automobile).
 * Token-overlap Jaccard would miss those.
 *
 * The `similarity` field still carries the Jaccard score so callers
 * that want stricter behaviour (e.g. the trie ranker's score boost)
 * can gate on it, but the pill in the UI fires on time-proximity
 * alone which is what the operator described.
 *
 * @returns {{ isRefinement: boolean, similarity: number, anchoredTo: string | null }}
 */
export function detectRefinement(partial) {
  const p = normalise(partial);
  if (!p) return { isRefinement: false, similarity: 0, anchoredTo: null };
  const pTokens = tokenise(p);
  const recents = getRefinementRecents();
  if (recents.length === 0) {
    return { isRefinement: false, similarity: 0, anchoredTo: null };
  }
  // Most-recent query anchors the pill — any query in the 60 s window
  // counts as a refinement candidate. Similarity is still measured so
  // downstream ranking can gate on it, but the pill fires on the
  // time-proximity signal alone.
  const anchor = recents[0];
  const anchorSim = jaccard(pTokens, tokenise(anchor.q));
  return {
    isRefinement: true,
    similarity: anchorSim,
    anchoredTo: anchor.q,
  };
}

// ─── Public constants (exported for tests) ────────────────────────
export const CONSTANTS = Object.freeze({
  REFINEMENT_WINDOW_MS,
  REFINEMENT_MAX_RECENTS,
  REFINEMENT_JACCARD_MIN,
  REFINEMENT_SCORE_BOOST,
  EXACT_RECENT_BOOST,
  SESSION_RECENT_KEY,
  SESSION_KA_CACHE_KEY,
});
